import { useEffect, useState } from "react";
import { ApiError, revokePortal } from "../../api";

interface DocPortalRevokeModalProps {
  dealId: string;
  /** Display name from the active portal — used inline in the warning text. */
  borrowerName: string;
  /** Fires on successful revoke with the server-reported timestamp. Parent
   *  is expected to close the modal, refresh the tab, and toast. */
  onConfirmed: (revokedAt: string) => void;
  onCancel: () => void;
}

export function DocPortalRevokeModal({
  dealId,
  borrowerName,
  onConfirmed,
  onCancel,
}: DocPortalRevokeModalProps) {
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Generated once per modal mount and reused across retries. Critical: if a
  // retry sent a fresh key, Portal would treat it as a new action and double-
  // process. Same key → Portal's 24h dedupe collapses retries into one revoke.
  const [idempotencyKey] = useState(() => crypto.randomUUID());

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !submitting) onCancel();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onCancel, submitting]);

  const handleConfirm = async () => {
    setError(null);
    setSubmitting(true);
    try {
      const trimmed = reason.trim();
      const result = await revokePortal(
        dealId,
        trimmed.length > 0 ? trimmed : undefined,
        idempotencyKey,
      );
      onConfirmed(result.revoked_at);
      // Parent unmounts on success — no need to setSubmitting(false).
    } catch (err) {
      setError(
        err instanceof ApiError
          ? `${err.code} (HTTP ${err.status})`
          : err instanceof Error
            ? err.message
            : "revoke_failed",
      );
      setSubmitting(false);
    }
  };

  return (
    <div className="write-confirm-backdrop" role="dialog" aria-modal="true">
      <div className="write-confirm-card doc-portal-revoke-card">
        <div className="write-confirm-title destructive">
          REVOKE DOCUMENT PORTAL
        </div>
        <div className="doc-portal-revoke-body">
          This will immediately kill {borrowerName}'s magic link. They won't be
          able to upload further documents or view existing ones. This action
          cannot be undone.
        </div>
        <label className="doc-portal-revoke-label">
          <span>Why are you revoking? (optional — helpful for audit)</span>
          <textarea
            className="doc-portal-revoke-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Borrower switched lenders, deal withdrawn, sent in error…"
            rows={3}
            disabled={submitting}
            autoFocus
          />
        </label>
        {error && <div className="write-confirm-error">⚠ {error}</div>}
        <div className="write-confirm-actions">
          <button
            className="write-confirm-cancel"
            onClick={onCancel}
            disabled={submitting}
          >
            CANCEL
          </button>
          <button
            className="write-confirm-confirm confirm-delete-confirm"
            onClick={handleConfirm}
            disabled={submitting}
          >
            {submitting ? "REVOKING…" : "REVOKE PORTAL"}
          </button>
        </div>
      </div>
    </div>
  );
}
